import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import * as Sentry from '@sentry/react'

import { useAuthStore } from '../stores/useAuthStore'
import { useB2BAuthStore } from '../stores/useB2BAuthStore'

/**
 * Sentry feedback launcher (bottom-right corner). Visibility rules:
 *   - B2B logged-in member: every /b2b/* page
 *   - C2C logged-in user:   /profile* and /interview* only
 *
 * The widget mounts/unmounts on every route or auth change so the launcher
 * disappears on pages where it shouldn't be shown.
 *
 * Each open is tagged with `audience: b2b|c2c` plus the relevant identifier
 * (workspace_id for B2B, user_id for C2C) so the Sentry inbox can be filtered.
 *
 * The Sentry SDK is loaded only when `VITE_SENTRY_DSN` is set — without a DSN
 * `Sentry.getFeedback()` returns undefined and this component renders nothing,
 * which is the correct behaviour for local dev without Sentry credentials.
 */
const C2C_VISIBLE_PREFIXES = ['/profile', '/interview']

function resolveAudience(pathname, c2cUser, b2bMember) {
  if (b2bMember && pathname.startsWith('/b2b/')) return 'b2b'
  if (c2cUser && C2C_VISIBLE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
    return 'c2c'
  }
  return null
}

export default function FeedbackWidget() {
  const { pathname } = useLocation()
  const c2cUser = useAuthStore((s) => s.user)
  const b2bMember = useB2BAuthStore((s) => s.member)
  const b2bWorkspace = useB2BAuthStore((s) => s.workspace)
  const widgetRef = useRef(null)

  useEffect(() => {
    const audience = resolveAudience(pathname, c2cUser, b2bMember)

    // Tear down previous widget when route/auth makes it ineligible.
    if (!audience) {
      if (widgetRef.current) {
        widgetRef.current.removeFromDom?.()
        widgetRef.current = null
      }
      return undefined
    }

    // No-op if Sentry SDK is not initialized (no VITE_SENTRY_DSN).
    const feedback = typeof Sentry.getFeedback === 'function' ? Sentry.getFeedback() : null
    if (!feedback) return undefined

    // Set user/tag context for any event captured from this scope (errors,
    // replays AND feedback). The user prefix avoids collisions when the same
    // email is logged in to both products on the same machine.
    if (audience === 'b2b' && b2bMember) {
      Sentry.setUser({
        id: `b2b:${b2bMember.id}`,
        email: b2bMember.email,
        username: b2bMember.name || undefined,
      })
      Sentry.setTag('audience', 'b2b')
      Sentry.setTag('workspace_id', b2bWorkspace?.id || null)
      Sentry.setTag('workspace_slug', b2bWorkspace?.slug || null)
    } else if (audience === 'c2c' && c2cUser) {
      Sentry.setUser({
        id: `c2c:${c2cUser.id}`,
        email: c2cUser.email,
        username: c2cUser.name || undefined,
      })
      Sentry.setTag('audience', 'c2c')
      Sentry.setTag('workspace_id', null)
      Sentry.setTag('workspace_slug', null)
    }

    // Recreate widget on every audience switch so the launcher always reflects
    // the current scope. createWidget is cheap; the alternative (re-tagging a
    // shared widget) leaves stale tags around when navigating C2C ↔ B2B.
    if (widgetRef.current) {
      widgetRef.current.removeFromDom?.()
      widgetRef.current = null
    }
    widgetRef.current = feedback.createWidget()

    return () => {
      widgetRef.current?.removeFromDom?.()
      widgetRef.current = null
    }
  }, [pathname, c2cUser, b2bMember, b2bWorkspace])

  return null
}
